// NEW_FILE_JUSTIFIED: Read-only renderer for repeatable_group fields in SectionedPolicyForm readOnly mode — distinct from RepeatableGroupField (edit mode) and kept separate to preserve the 200-line limit on SectionedPolicyForm.tsx
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import type { IBuilderField, IBuilderInnerField } from '@/@types/insuranceBuilder';

function formatValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) return v.length === 0 ? '—' : v.join(', ');
  return String(v);
}

interface IRowFieldProps {
  // LOCAL_TYPE_JUSTIFIED: private sub-component props — too tightly coupled to this file to extract to @types
  field: IBuilderInnerField;
  value: unknown;
}

function RowField({ field, value }: IRowFieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <Label className="text-xs text-muted-foreground">{field.label}</Label>
      <div
        className={cn('rounded-md border bg-muted/40 px-3 py-2 text-sm cursor-not-allowed')}
        aria-readonly="true"
      >
        {formatValue(value)}
      </div>
    </div>
  );
}

export interface IReadOnlyRepeatableGroupProps {
  // LOCAL_TYPE_JUSTIFIED: exported props for ReadOnlyRepeatableGroup component — sibling file used only in SectionedPolicyForm; co-locating avoids premature abstraction
  field: IBuilderField;
  value: unknown;
}

function toRecord(v: unknown): Record<string, unknown> {
  if (typeof v === 'object' && v !== null && !Array.isArray(v)) {
    return v as Record<string, unknown>; // AS_JUSTIFIED: runtime guard above confirms plain object; cast is safe
  }
  return {};
}

export function ReadOnlyRepeatableGroup({ field, value }: IReadOnlyRepeatableGroupProps) {
  const rows = Array.isArray(value) ? value : [];
  const groupFields = field.groupFields ?? [];

  if (rows.length === 0) {
    return <p className={cn('text-sm', 'text-muted-foreground')}>—</p>;
  }

  return (
    <div className="flex flex-col gap-3">
      {rows.map((row, rowIndex) => {
        const rowRecord = toRecord(row);
        return (
          <Card key={rowIndex} className="p-0">
            <CardContent className="grid grid-cols-1 gap-3 p-4 md:grid-cols-2">
              {groupFields.map(sub => (
                <RowField key={sub.fieldKey} field={sub} value={rowRecord[sub.fieldKey] ?? null} />
              ))}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
